import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/rootReducer";
import useApi from "../../api";
import { toast } from "react-toastify";
import { fetchWarehouse } from "../../redux/warehosueSlice";
import NxtbnDrawer from "../drawer";
import { InputField, NXForm } from "../common";
import SelectStyled from "../Select";
import { getEnumItem, makeEnumFriendly } from "../../enum";
import moment from "moment";
import ProductWithVariantSearch from "../common/productWithVariantSearchField";

import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client";

interface PurchaseEditProps {
    isOpen: boolean;
    onClose: () => void;
    refresh: () => void;
    pid: any;
}

const GET_PURCHASE_DETAILS = gql`
    query GetPurchaseDetails($id: ID!) {
        purchase(id: $id) {
            createdAt
            dbId
            destination {
                dbId
                name
            }
            supplier {
                dbId
                name
            }
            totalCost
            items(first: 100) {
                edges {
                    node {
                        dbId
                        orderedQuantity
                        unitCost
                        variant {
                            dbId
                            price
                        }
                    }
                }
            }
        }
    }
`;


function PurchaseEdit({ isOpen, onClose, refresh, pid }: PurchaseEditProps) {
    const [formData, setFormData] = useState<any>({ items: [] });
    const [errorData, setErrorData] = useState<any>({});
    const [suppliers, setSuppliers] = useState<any>();
    const warehouses = useSelector((state: RootState) => state.warehouse);

    const api = useApi();
    const dispatch = useDispatch();

    const { data, loading, error, refetch } = useQuery(GET_PURCHASE_DETAILS, {
            variables: { id: pid },
            notifyOnNetworkStatusChange: true,
        });

    const getSuppliers = () => {
        api.getSuppliers()
            .then((response: any) => {
                setSuppliers(response.results);
            })
            .catch(() => toast.error('Error fetching supplier.'));
    };

    const getWarehouses = () => {
        dispatch(fetchWarehouse() as any);
    };

    useEffect(() => {
        if (isOpen) {
            getSuppliers();
            getWarehouses();
        }
    }, [isOpen]);

    useEffect(() => {
        if (data) {
            setFormData({
                supplier: data.purchase.supplier.dbId,
                destination: data.purchase.destination.dbId,
                destination_name: data.purchase.destination.name,
                total_cost: data.purchase.totalCost,
                expected_delivery_date: moment(data.purchase.createdAt).format('YYYY-MM-DD'),
                items: data.purchase.items.edges.map((item: any) => ({
                    variant: item.node.variant.dbId,
                    id: item.node.dbId,
                    quantity: item.node.orderedQuantity,
                    ordered_quantity: item.node.orderedQuantity,
                    price: item.node.variant.price,
                    unit_cost: item.node.unitCost,
                })),
            });
        }
    }, [data]);

    

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        api.updatePurchase(pid, formData).then(
            () => {
                refresh();
                refetch();
                toast.success("Supplier Updated Successfully!");
                onClose();
                setFormData({ items: [{}] });
            },
            (error) => {
                setErrorData(error.response.data);
            }
        );
    };

    const handleChange = (e: any) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSelectChange = (name: any, value: any) => {
        setFormData((prev: any) => ({ ...prev, [name]: value }));
    };

    const onVariantChange = (variantData: any[]) => {
        const updatedVariantData = variantData.map((item) => ({
            ...item,
            variant: item.id,
            ordered_quantity: item.quantity,
        }));
        setFormData((prev: any) => ({ ...prev, items: updatedVariantData }));
    };


    return (
        <NxtbnDrawer
            isOpen={isOpen}
            onClose={() => {
                onClose();
                setFormData({ items: [{}] });
            }}
            title={pid ? 'Edit Purchase Order' : 'Create Purchase Order'}
        >
            <NXForm onSubmit={handleSubmit} className="p-6" title="Create Purchase Order">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label htmlFor="supplier" className="block text-sm font-medium text-gray-700">
                            Supplier
                        </label>
                        {suppliers && formData?.supplier &&
                            <SelectStyled
                                name="supplier"
                                errorData={errorData}
                                defaultValue={getEnumItem(suppliers, formData?.supplier)}
                                options={makeEnumFriendly(suppliers || [])}
                                onChange={(e) => handleSelectChange('supplier', e.value)}
                                className="mt-1"
                            />
                        }
                    </div>

                    <div>
                        <label htmlFor="destination" className="block text-sm font-medium text-gray-700">
                            Destination
                        </label>
                        {warehouses.results && formData?.destination && (
                            <SelectStyled
                                name="destination"
                                errorData={errorData}
                                defaultValue={{ value: formData?.destination, label: formData?.destination_name }}
                                options={makeEnumFriendly(warehouses.results || [])}
                                onChange={(e) => handleSelectChange('destination', e.value)}
                                className="mt-1"
                            />
                        )}
                    </div>

                    <div>
                        <label htmlFor="expected_delivery_date" className="block text-sm font-medium text-gray-700">
                            Estimated Arrival
                        </label>
                        <InputField
                            type="datetime-local"
                            id="expected_delivery_date"
                            name="expected_delivery_date"
                            errorData={errorData}
                            defaultValue={moment(formData?.expected_delivery_date).format('YYYY-MM-DDTHH:mm')}
                            onChange={(e) => {
                                setFormData((prev: any) => ({
                                    ...prev,
                                    expected_delivery_date: moment(e.target.value).format('YYYY-MM-DD'),
                                }));
                            }}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>

                    <div>
                        <label htmlFor="total_cost" className="block text-sm font-medium text-gray-700">
                            Total Cost
                        </label>
                        <InputField
                            type="number"
                            id="total_cost"
                            name="total_cost"
                            errorData={errorData}
                            defaultValue={formData?.total_cost}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                </div>

                <div className="mb-6">
                    <ProductWithVariantSearch value={formData.items} usedIn="purchase" onVariantChange={onVariantChange} />
                </div>

                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="mr-3 px-4 py-2 text-[#0CAF60] border border-[#0CAF60] hover:bg-slate-200 text-sm font-medium rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="inline-flex justify-center rounded-md border border-transparent px-4 py-2 bg-primary-600 text-base font-medium text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                    >
                        Save as Draft
                    </button>
                </div>
            </NXForm>
        </NxtbnDrawer>
    );
}

export default PurchaseEdit;
